import { memo } from 'react';
import { useImageBrightness } from '@/hooks/use-image-brightness';
import { useVehicleCard } from './vehicle-card.hook';
import {
	vehicleCardStyles,
	cardContentStyles,
	cardInfoStyles,
	cardActionsContainerStyles,
	styles
} from './vehicle-card.styles';
import {
	CardImage,
	CardInfo,
	CardMetrics,
	CardPrice,
	CardStatusTag,
	CardActions,
	CardOptionsMenu
} from './components';
import { MENU_ICON_BY_ID } from './icons';
import { myAdsTestId } from '../../utils';
import type { TVehicleCardConfig } from './vehicle-card.types';
import type { TMenuOption } from './components/card-options-menu';

const VehicleCardComponent = ({ ad, layout = 'desktop', onAction, onMenuAction }: TVehicleCardConfig) => {
	const {
		formattedPrice,
		yearDisplay,
		showMetrics,
		availableActions,
		menuOptions,
		menuDisabled,
		handleAction,
		handleMenuAction
	} = useVehicleCard({ ad, onAction, onMenuAction });

	const imageTone = useImageBrightness(layout === 'mobile' ? ad.photoUrl : undefined);
	const overlayIconColor = imageTone === 'dark' ? '#ffffff' : '#2e2d37';

	const title = `${ad.brandName} ${ad.modelName}`;

	const dropdownOptions: TMenuOption[] = menuOptions.map((opt) => ({
		id: opt.id,
		label: opt.label,
		icon: MENU_ICON_BY_ID[opt.id],
		onSelect: () => handleMenuAction(opt.id)
	}));

	if (layout === 'mobile') {
		return (
			<article
				className={vehicleCardStyles({ layout: 'mobile' })}
				data-testid={myAdsTestId.vehicleCard(ad.id)}
			>
				<div className={styles.mobileImageWrapper}>
					<CardImage
						src={ad.photoUrl}
						alt={title}
						layout="mobile"
						vehicleType={ad.vehicleType}
						adId={ad.id}
					/>
					<div className={styles.menuButtonAbsolute}>
						<CardOptionsMenu
							adId={ad.id}
							options={dropdownOptions}
							disabled={menuDisabled}
							tone="overlay"
							iconColor={overlayIconColor}
						/>
					</div>
				</div>

				<div className={styles.mobileContent}>
					<CardInfo adId={ad.id} title={title} version={ad.versionName} yearDisplay={yearDisplay} />

					{showMetrics && (
						<CardMetrics
							adId={ad.id}
							views={ad.views}
							interestedCount={ad.interestedCount}
							createdAt={ad.createdAt}
						/>
					)}

					<div className={styles.priceContainer}>
						<CardPrice adId={ad.id} formattedPrice={formattedPrice} />
						<CardStatusTag adId={ad.id} status={ad.displayStatus} />
					</div>

					<CardActions
						adId={ad.id}
						actions={availableActions}
						status={ad.displayStatus}
						onAction={handleAction}
					/>
				</div>
			</article>
		);
	}

	return (
		<article
			className={vehicleCardStyles({ layout: 'desktop' })}
			data-testid={myAdsTestId.vehicleCard(ad.id)}
		>
			<CardImage
				src={ad.photoUrl}
				alt={title}
				layout="desktop"
				vehicleType={ad.vehicleType}
				adId={ad.id}
			/>

			<div className={cardContentStyles({ layout: 'desktop' })}>
				<div className={cardInfoStyles({ layout: 'desktop' })}>
					<CardInfo adId={ad.id} title={title} version={ad.versionName} yearDisplay={yearDisplay} />

					{showMetrics && (
						<CardMetrics
							adId={ad.id}
							views={ad.views}
							interestedCount={ad.interestedCount}
							createdAt={ad.createdAt}
						/>
					)}

					<CardPrice adId={ad.id} formattedPrice={formattedPrice} />
				</div>

				<div className={cardActionsContainerStyles({ layout: 'desktop' })}>
					<div className={styles.statusRow}>
						<CardStatusTag adId={ad.id} status={ad.displayStatus} />
						<CardOptionsMenu adId={ad.id} options={dropdownOptions} disabled={menuDisabled} />
					</div>

					<CardActions
						adId={ad.id}
						actions={availableActions}
						status={ad.displayStatus}
						onAction={handleAction}
					/>
				</div>
			</div>
		</article>
	);
};

VehicleCardComponent.displayName = 'VehicleCard';

const VehicleCard = memo(VehicleCardComponent);

export { VehicleCard };
